import { useEffect, useState } from 'react';

type Locale = 'de' | 'en' | 'pl';

type Dictionary = Record<string, string>;

const STORAGE_KEY = 'planning-poker:locale';
const FALLBACK_LOCALE: Locale = 'de';

const translations: Record<Locale, Dictionary> = {
    de: {
        'app.brand': 'Planning Poker by Prime Force',
        'app.scrumGuide': 'Scrum Guide',
        'app.imprint': 'Impressum',
        'app.footer': '© {year} Prime Force Freiburg GmbH - Planning Poker – Deck-Auswahl',

        // Theme toggle
        'theme.professional.label': 'Professionell',
        'theme.professional.title': 'Professionelles Design',
        'theme.poker.label': 'Poker',
        'theme.poker.title': 'Casino Poker Design',
        'theme.dark.label': 'Dunkel',
        'theme.dark.title': 'Dunkles Design',

        'banner.connecting': 'Verbinde...',
        'banner.disconnected': 'Verbindung getrennt – versuche, wieder zu verbinden …',
        'banner.reconnected': 'Wieder verbunden',
        'banner.error': 'Verbindungsfehler',

        'room.link.copied': 'Raum-Link kopiert: {url}',
        'room.name.label': 'Dein Anzeigename',
        'room.name.placeholder': 'z.B. Dietmar',
        'room.join.button': 'Beitreten',
        'room.header.room': 'Raum',
        'room.header.round': 'Runde',
        'room.header.selected': 'ausgewählt',
        'room.header.revealed': 'Aufgedeckt',
        'room.header.hidden': 'Verdeckt',
        'room.actions.share': 'Link teilen',
        'room.actions.reveal': 'Umdrehen',
        'room.actions.newRound': 'Neue Runde',
        'room.deck.title': 'Deck ({deck})',
        'room.deck.hint.revealed': 'Nach dem Umdrehen kannst du deine Meinung ändern.',
        'room.deck.hint.hidden': 'Karten bleiben verdeckt bis alle gewählt haben oder jemand „Umdrehen“ drückt.',
        'room.deck.clear': 'Zurück-ziehen',

        // Flip cards / participants
        'flip.away': '(abwesend)',
        'flip.selected': '✅ gewählt',

        // Statistics
        'stats.title': 'Statistiken (nach Aufdecken)',
        'stats.consensus': 'Konsens',
        'stats.distribution': 'Stimmenverteilung',
        'stats.vote.one': '{count} Stimme',
        'stats.vote.other': '{count} Stimmen',
        'stats.avg': 'Durchschnitt',
        'stats.median': 'Median',
        'stats.min': 'Min',
        'stats.max': 'Max',

        // Lobby
        'lobby.create.title': 'Neuen Raum erstellen',
        'lobby.create.desc': 'Erstellt immer eine neue, eindeutige Raum-ID. Optional kannst du einen Titel vergeben.',
        'lobby.create.label': 'Titel (optional)',
        'lobby.create.placeholder': 'z. B. Sprint 42 – Schätzrunde',
        'lobby.create.deck.label': 'Deck',
        'lobby.create.button': 'Raum erstellen',

        'lobby.join.title': 'Raum beitreten',
        'lobby.join.desc': 'Gib eine Raum-ID oder einen Raum-Link ein.',
        'lobby.join.label': 'Room-ID oder Link',
        'lobby.join.placeholder': 'z. B. https://poker.example.com/r/abc123',
        'lobby.join.button': 'Raum beitreten',
        'lobby.join.invalid': 'Bitte eine gültige Room-ID oder einen gültigen Raum-Link eingeben.',

        // Decks
        'deck.modified': 'Modified Fibonacci',
        'deck.fibonacci': 'Fibonacci',
        'deck.tshirt': 'T-Shirt Größen',

        // Impressum
        'imprint.legalNotice': 'Rechtliche Hinweise',
        'imprint.title': 'Impressum',
        'imprint.backHome': 'Zurück zur Hauptseite',
        'imprint.responsible.title': 'Verantwortlich für den Inhalt',
        'imprint.responsible.address': 'Prime Force Freiburg GmbH\nGartenstr. 21\n79365 Rheinhausen\nDeutschland',
        'imprint.contact.title': 'Kontakt',
        'imprint.contact.email': 'E-Mail: info-de-freiburg@prime-force.com',
        'imprint.contact.management': 'Geschäftsführer: Dietmar Enghauser, Uwe Fabig',
        'imprint.contact.register': 'Handelsregister: Freiburg i. Br. HRB 716412',
        'imprint.contact.vat': 'Umsatzsteuer-ID: DE 312 282 630',
        'imprint.liability.title': 'Haftungshinweis',
        'imprint.liability.text': 'Trotz sorgfältiger inhaltlicher Kontrolle übernehmen wir keine Haftung für die Inhalte externer Links. Für den Inhalt der verlinkten Seiten sind ausschließlich deren Betreiber verantwortlich.',
        'imprint.privacy.title': 'Datenschutzhinweis',
        'imprint.privacy.text': 'Wir verwenden keinerlei Cookies und verarbeiten auch keine personenbezogenen Daten. Alle Funktionen der Anwendung lassen sich vollständig ohne personenbezogene Identifikation nutzen.'
    },
    en: {
        'app.brand': 'Planning Poker by Prime Force',
        'app.scrumGuide': 'Scrum Guide',
        'app.imprint': 'Imprint',
        'app.footer': '© {year} Prime Force Freiburg GmbH - Planning Poker – Deck selection',

        // Theme toggle
        'theme.professional.label': 'Professional',
        'theme.professional.title': 'Professional design',
        'theme.poker.label': 'Poker',
        'theme.poker.title': 'Casino poker design',
        'theme.dark.label': 'Dark',
        'theme.dark.title': 'Dark theme',

        'banner.connecting': 'Connecting...',
        'banner.disconnected': 'Connection lost – trying to reconnect…',
        'banner.reconnected': 'Reconnected',
        'banner.error': 'Connection error',

        'room.link.copied': 'Room link copied: {url}',
        'room.name.label': 'Your display name',
        'room.name.placeholder': 'e.g. Dietmar',
        'room.join.button': 'Join',
        'room.header.room': 'Room',
        'room.header.round': 'Round',
        'room.header.selected': 'selected',
        'room.header.revealed': 'Revealed',
        'room.header.hidden': 'Hidden',
        'room.actions.share': 'Share link',
        'room.actions.reveal': 'Reveal',
        'room.actions.newRound': 'New round',
        'room.deck.title': 'Deck ({deck})',
        'room.deck.hint.revealed': 'After revealing you can still change your mind.',
        'room.deck.hint.hidden': 'Cards stay hidden until everyone selected or someone presses “Reveal”.',
        'room.deck.clear': 'Pull back',

        // Flip cards / participants
        'flip.away': '(away)',
        'flip.selected': '✅ selected',

        // Statistics
        'stats.title': 'Statistics (after reveal)',
        'stats.consensus': 'Consensus',
        'stats.distribution': 'Vote distribution',
        'stats.vote.one': '{count} vote',
        'stats.vote.other': '{count} votes',
        'stats.avg': 'Average',
        'stats.median': 'Median',
        'stats.min': 'Min',
        'stats.max': 'Max',

        // Lobby
        'lobby.create.title': 'Create a new room',
        'lobby.create.desc': 'Always creates a new unique room ID. You can optionally provide a title.',
        'lobby.create.label': 'Title (optional)',
        'lobby.create.placeholder': 'e.g. Sprint 42 – Estimation round',
        'lobby.create.deck.label': 'Deck',
        'lobby.create.button': 'Create Room',

        'lobby.join.title': 'Join a room',
        'lobby.join.desc': 'Enter a room ID or a room link.',
        'lobby.join.label': 'Room ID or link',
        'lobby.join.placeholder': 'e.g. https://poker.example.com/r/abc123',
        'lobby.join.button': 'Join Room',
        'lobby.join.invalid': 'Please enter a valid room ID or room link.',

        // Decks
        'deck.modified': 'Modified Fibonacci',
        'deck.fibonacci': 'Fibonacci',
        'deck.tshirt': 'T-Shirt sizes',

        // Impressum
        'imprint.legalNotice': 'Legal notice',
        'imprint.title': 'Imprint',
        'imprint.backHome': 'Back to main page',
        'imprint.responsible.title': 'Responsible for the content',
        'imprint.responsible.address': 'Prime Force Freiburg GmbH\nGartenstr. 21\n79365 Rheinhausen\nGermany',
        'imprint.contact.title': 'Contact',
        'imprint.contact.email': 'Email: info-de-freiburg@prime-force.com',
        'imprint.contact.management': 'Managing directors: Dietmar Enghauser, Uwe Fabig',
        'imprint.contact.register': 'Commercial register: Freiburg i. Br. HRB 716412',
        'imprint.contact.vat': 'VAT ID: DE 312 282 630',
        'imprint.liability.title': 'Liability notice',
        'imprint.liability.text': 'Despite careful content control, we assume no liability for the content of external links. The operators of the linked pages are solely responsible for their content.',
        'imprint.privacy.title': 'Privacy notice',
        'imprint.privacy.text': 'We do not use cookies and do not process any personal data. All functions of the application can be used entirely without personal identification.'
    },
    pl: {
        'app.brand': 'Planning Poker by Prime Force',
        'app.scrumGuide': 'Przewodnik Scrum',
        'app.imprint': 'Informacje prawne',
        'app.footer': '© {year} Prime Force Freiburg GmbH - Planning Poker – Wybór talii',

        // Theme toggle
        'theme.professional.label': 'Professional',
        'theme.professional.title': 'Profesjonalny wygląd',
        'theme.poker.label': 'Poker',
        'theme.poker.title': 'Wygląd kasynowy',
        'theme.dark.label': 'Dark',
        'theme.dark.title': 'Ciemny motyw',

        'banner.connecting': 'Łączenie...',
        'banner.disconnected': 'Połączenie przerwane – próbuję ponownie…',
        'banner.reconnected': 'Połączono ponownie',
        'banner.error': 'Błąd połączenia',

        'room.link.copied': 'Skopiowano link do pokoju: {url}',
        'room.name.label': 'Twoja nazwa wyświetlana',
        'room.name.placeholder': 'np. Dietmar',
        'room.join.button': 'Dołącz',
        'room.header.room': 'Pokój',
        'room.header.round': 'Runda',
        'room.header.selected': 'wybrane',
        'room.header.revealed': 'Odkryte',
        'room.header.hidden': 'Zakryte',
        'room.actions.share': 'Udostępnij link',
        'room.actions.reveal': 'Odkryj',
        'room.actions.newRound': 'Nowa runda',
        'room.deck.title': 'Talia ({deck})',
        'room.deck.hint.revealed': 'Po odkryciu nadal możesz zmienić zdanie.',
        'room.deck.hint.hidden': 'Karty pozostaną zakryte, dopóki wszyscy nie wybiorą lub ktoś nie kliknie „Odkryj”.',
        'room.deck.clear': 'Wycofaj',

        // Flip cards / participants
        'flip.away': '(nieobecny)',
        'flip.selected': '✅ wybrane',

        // Statistics
        'stats.title': 'Statystyki (po odkryciu)',
        'stats.consensus': 'Konsensus',
        'stats.distribution': 'Rozkład głosów',
        'stats.vote.one': '{count} głos',
        'stats.vote.other': '{count} głosy',
        'stats.avg': 'Średnia',
        'stats.median': 'Mediana',
        'stats.min': 'Min',
        'stats.max': 'Max',

        // Lobby
        'lobby.create.title': 'Utwórz nowy pokój',
        'lobby.create.desc': 'Tworzy zawsze nowy, unikalny identyfikator pokoju. Opcjonalnie możesz podać tytuł.',
        'lobby.create.label': 'Tytuł (opcjonalnie)',
        'lobby.create.placeholder': 'np. Sprint 42 – runda estymacji',
        'lobby.create.deck.label': 'Talia',
        'lobby.create.button': 'Utwórz pokój',

        'lobby.join.title': 'Dołącz do pokoju',
        'lobby.join.desc': 'Wpisz ID pokoju lub link do pokoju.',
        'lobby.join.label': 'ID pokoju lub link',
        'lobby.join.placeholder': 'np. https://poker.example.com/r/abc123',
        'lobby.join.button': 'Dołącz',
        'lobby.join.invalid': 'Wpisz prawidłowe ID pokoju lub link do pokoju.',

        // Decks
        'deck.modified': 'Modified Fibonacci',
        'deck.fibonacci': 'Fibonacci',
        'deck.tshirt': 'Rozmiary T-shirt',

        // Impressum
        'imprint.legalNotice': 'Informacje prawne',
        'imprint.title': 'Impressum',
        'imprint.backHome': 'Powrót do strony głównej',
        'imprint.responsible.title': 'Odpowiedzialny za treść',
        'imprint.responsible.address': 'Prime Force Freiburg GmbH\nGartenstr. 21\n79365 Rheinhausen\nNiemcy',
        'imprint.contact.title': 'Kontakt',
        'imprint.contact.email': 'E-mail: info-de-freiburg@prime-force.com',
        'imprint.contact.management': 'Zarząd: Dietmar Enghauser, Uwe Fabig',
        'imprint.contact.register': 'Rejestr handlowy: Freiburg i. Br. HRB 716412',
        'imprint.contact.vat': 'NIP: DE 312 282 630',
        'imprint.liability.title': 'Uwagi dotyczące odpowiedzialności',
        'imprint.liability.text': 'Mimo starannej kontroli treści nie ponosimy odpowiedzialności za treść zewnętrznych linków. Za treści stron, do których prowadzą linki, odpowiadają wyłącznie ich operatorzy.',
        'imprint.privacy.title': 'Informacje o prywatności',
        'imprint.privacy.text': 'Nie używamy plików cookie ani nie przetwarzamy danych osobowych. Wszystkie funkcje aplikacji można używać w pełni bez identyfikacji osobistej.'
    }
};

function detectInitialLocale(): Locale {
    const stored = localStorage.getItem(STORAGE_KEY) as Locale | null;
    if (stored && stored in translations) return stored;
    const nav = navigator.language.slice(0, 2).toLowerCase();
    if (nav === 'de' || nav === 'en' || nav === 'pl') return nav;
    return FALLBACK_LOCALE;
}

let currentLocale: Locale = detectInitialLocale();
const listeners = new Set<(locale: Locale) => void>();

function notify(locale: Locale) {
    listeners.forEach(fn => fn(locale));
}

function interpolate(template: string, vars?: Record<string, string | number>) {
    if (!vars) return template;
    return template.replace(/\{(\w+)\}/g, (_m, key) => String(vars[key] ?? ''));
}

export function t(key: string, vars?: Record<string, string | number>): string {
    const dict = translations[currentLocale] ?? translations[FALLBACK_LOCALE];
    const fallbackDict = translations[FALLBACK_LOCALE];
    const template = dict[key] ?? fallbackDict[key] ?? key;
    return interpolate(template, vars);
}

export function setLocale(locale: Locale) {
    if (!(locale in translations)) return;
    currentLocale = locale;
    localStorage.setItem(STORAGE_KEY, locale);
    notify(locale);
}

export function getLocale(): Locale {
    return currentLocale;
}

export function subscribe(listener: (locale: Locale) => void) {
    listeners.add(listener);
    return () => listeners.delete(listener);
}

// React hook to rerender on locale changes (useful in components)
export function useLocale(): Locale {
    const [locale, setLocale] = useState<Locale>(getLocale());
    useEffect(() => subscribe(setLocale), []);
    return locale;
}

export type { Locale };
